/* eslint-disable arrow-body-style */
import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuthContext } from '../../auth/AuthContext';
import type { ErrorMessages, Rules } from '../../config/types';
import { textinputMaxLength } from '../../constants';
import { validateEmailDomain as validateEmailDomainRequest } from '../../service/form';
import { ApiValidationError } from '../../utils/api-error';
import { getShareAllowedEmailDomainsForTenant } from '../../config';
import {
  allowedDomainValidationMessage,
  emailMatchesAllowedDomains,
} from '../../utils/email-domain-utils';
import {
  containsReservedCharacters,
  getCharacterCount,
  getInputRules,
  getWordCount,
  stripReservedCharacters,
  stringToObject,
  validateField,
} from '../../utils/form-utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
import Input from './TextInput';

const getCurrentTenantFromPath = (): string => {
  if (typeof window === 'undefined' || !window.location?.pathname) return '';
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  return String(pathParts[0] || '').trim().toLowerCase();
};
type Props = {
  name: string;
  title: string;
  width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter';
  rules?: Rules;
  approver?: boolean;
  allowSelfApprover?: boolean;
  restrictDomains?: boolean;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  autoFill?: {
    keys: string[];
    field: string;
  };
  userEmail?: boolean;
  wordCount?: number;
  characterCount?: number;
} & ComponentPropsWithoutRef<'input'>;

const TextInputField = (props: Props) => {
  const {
    name,
    title,
    rules,
    approver,
    allowSelfApprover,
    restrictDomains,
    disabled,
    defaultValue,
    errorMessages,
    detailsHeading,
    detailsContent,
    autoFill,
    className,
    readOnly,
    userEmail,
    wordCount,
    characterCount,
  } = props;

  const { fetchJWT } = useAuthContext();
  const {
    register,
    getValues,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  let type = 'text';
  if (rules?.number) type = 'number';
  else if (rules?.email) type = 'email';

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textinputMaxLength) }),
    [rules, title],
  );

  const allFields = getValues();

  // Watch all form changes except for the current field
  const formValues = watch(Object.keys(allFields).filter((key) => key !== name));

  // If this field is auto-filled by config, compute its value from the referenced object
  useEffect(() => {
    if (!autoFill) return;
    const valueKey = Object.keys(allFields).find((x) => x.endsWith(autoFill.field));
    if (!valueKey) return;
    const value = allFields[valueKey];
    if (!value) return;
    const values = stringToObject(value);
    if (!values) return;
    const total = Object.values(values).reduce((acc, curr) => acc + Number(curr), 0);
    setValue(name, total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, autoFill]);

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, [name, defaultValue, getValues, setValue]);
  useEffect(() => {
    if (!userEmail) return;
    const currentUserEmail = getCurrentUserEmail();
    if (!currentUserEmail) return;
    const currentValue = getValues(name);
    if (typeof currentValue === 'string' && currentValue.trim()) return;
    setValue(name, currentUserEmail, { shouldValidate: true, shouldDirty: false });
  }, [userEmail, getValues, name, setValue]);

  const getCurrentUserEmail = (): string | undefined => {
    // Stored by AuthContext on login; used here for client-side "self approver" validation.
    try {
      const raw = window?.localStorage?.getItem('activeAuthSession');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { userEmail?: unknown } | undefined;
      if (!parsed || typeof parsed.userEmail !== 'string') return undefined;
      const normalized = parsed.userEmail.trim().toLowerCase();
      return normalized || undefined;
    } catch (_err) {
      return undefined;
    }
  };

  const validateEmailDomain = async (value: string): Promise<string | undefined> => {
    if (!rules?.email) return undefined;
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) return undefined;

    try {
      const { idToken } = await fetchJWT({ redirectOnFail: false });
      if (!idToken) return undefined;
      await validateEmailDomainRequest(idToken, normalized);
      return undefined;
    } catch (err) {
      if (err instanceof ApiValidationError) {
        return err.message;
      }
      return undefined;
    }
  };

  const hooksFormRegister = register(name, {
    validate: (value: string) => {
      const baseError = validateField(title, inputRules, value, errorMessages);
      if (baseError) return baseError;

      const stringValue = typeof value === 'string' ? value : String(value ?? '');
      if (approver && rules?.email) {
        const currentUserEmail = getCurrentUserEmail();
        const normalizedIncoming = stringValue.trim().toLowerCase();
        if (
          allowSelfApprover !== true &&
          currentUserEmail &&
          normalizedIncoming &&
          normalizedIncoming === currentUserEmail
        ) {
          return `You cannot add yourself as ${title}`;
        }
      }

      // Default: restrict email questions to configured domains (if any).
      if (rules?.email && restrictDomains !== false) {
        const allowedDomains = getShareAllowedEmailDomainsForTenant(getCurrentTenantFromPath());
        if (
          Array.isArray(allowedDomains) &&
          allowedDomains.length > 0 &&
          !emailMatchesAllowedDomains(stringValue, allowedDomains)
        ) {
          return allowedDomainValidationMessage(allowedDomains);
        }
      }
      if (typeof wordCount === 'number') {
        const wordsUsed = getWordCount(stringValue);
        if (wordsUsed > wordCount) {
          const over = wordsUsed - wordCount;
          return `You have ${over} word${over === 1 ? '' : 's'} too many`;
        }
      }

      if (typeof characterCount === 'number') {
        const charsUsed = getCharacterCount(stringValue);
        if (charsUsed > characterCount) {
          const over = charsUsed - characterCount;
          return `You have ${over} character${over === 1 ? '' : 's'} too many`;
        }
      }

      return undefined;
    },
  });
  const error = (errors as any)?.[name];

  const rawValue = watch(name);
  const stringValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
  const wordsUsed = getWordCount(stringValue);
  const charsUsed = getCharacterCount(stringValue);
  const hasWordCount = typeof wordCount === 'number';
  const hasCharacterCount = typeof characterCount === 'number';
  const isWordOver = hasWordCount && wordsUsed > wordCount;
  const isCharacterOver = hasCharacterCount && charsUsed > characterCount;

  // Read-only when auto-filled (CSC style), but still allow page-level disabling
  const isAuto = !!autoFill || !!readOnly;
  const inputClassName = className || 'govuk-input';
  const readOnlyStyle = isAuto
    ? {
        backgroundColor: '#f3f2f1',
        color: '#0b0c0c',
      }
    : undefined;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <Input
        {...props}
        {...hooksFormRegister}
        // Keep page-level disabling, do not auto-disable for autoFill
        disabled={disabled ?? false}
        readOnly={isAuto}
        aria-readonly={isAuto || undefined}
        className={inputClassName}
        style={{
          ...(props.style || {}),
          ...(readOnlyStyle || {}),
        }}
        onBlur={async (e) => {
          hooksFormRegister.onBlur(e);
          if (!rules?.email) return;

          const isLocallyValid = await trigger(name);
          if (!isLocallyValid) return;

          const remoteError = await validateEmailDomain(e.target.value);
          if (remoteError) {
            setError(name, { type: 'validate', message: remoteError });
          } else {
            clearErrors(name);
          }
        }}
        onKeyDown={(e) => {
          // The e.key values are strings for control characters such as 'Backspace'
          // All non control characters will have a single character length
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules.reservedCharacters)) {
            e.preventDefault();
            const sanitized = stripReservedCharacters(
              clipboardData,
              inputRules.reservedCharacters,
            );
            const target = e.target as HTMLInputElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = `${target.value.slice(0, start)}${sanitized}${target.value.slice(
              end,
            )}`;
            setValue(name, nextValue, { shouldDirty: true, shouldValidate: true });
            requestAnimationFrame(() => {
              const cursor = start + sanitized.length;
              target.selectionStart = cursor;
              target.selectionEnd = cursor;
            });
          }
        }}
        numeric={!!rules?.number}
        type={type}
      />
      {hasWordCount && (
        <p className={isWordOver ? 'govuk-error-message' : 'govuk-hint'} style={{ marginTop: 0 }}>
          {isWordOver
            ? `You have ${wordsUsed - wordCount} word${
                wordsUsed - wordCount === 1 ? '' : 's'
              } too many`
            : `You have ${wordCount - wordsUsed} word${
                wordCount - wordsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
      {hasCharacterCount && (
        <p
          className={isCharacterOver ? 'govuk-error-message' : 'govuk-hint'}
          style={{ marginTop: 0 }}
        >
          {isCharacterOver
            ? `You have ${charsUsed - characterCount} character${
                charsUsed - characterCount === 1 ? '' : 's'
              } too many`
            : `You have ${characterCount - charsUsed} character${
                characterCount - charsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
    </>
  );
};
TextInputField.displayName = 'TextInputField';

export default TextInputField;

